<script setup lang="ts">
import Typography from '@components/Typography.vue';
import { trans } from '@tools/translation.ts';
import { ref, onMounted, nextTick } from 'vue';
import { useFilterStore } from '@stores/useFilterStore.ts';
import { storeToRefs } from 'pinia';

const filterStore = useFilterStore();
const { teamSizeMin, teamSizeMax } = storeToRefs(filterStore);

const minInput = ref<HTMLInputElement | null>(null);

onMounted(async () => {
  await nextTick();

  minInput.value?.focus();
});

const getAutoAdjustedTeamSizeMax = (teamSize: number): number => {
  if (teamSize > 50) {
    return teamSize + 20;
  }

  if (teamSize > 25) {
    return teamSize + 10;
  }

  const teamSizeMappings = new Map([
    [ 1, 2 ], [ 2, 3 ], [ 3, 4 ], [ 4, 6 ], [ 5, 7 ], [ 6, 8 ], [ 7, 10 ], [ 8, 12 ],
    [ 9, 12 ], [ 10, 14 ], [ 11, 15 ], [ 12, 16 ], [ 13, 18 ], [ 14, 18 ], [ 15, 20 ],
    [ 16, 20 ], [ 17, 22 ], [ 18, 24 ], [ 19, 25 ], [ 20, 25 ], [ 21, 26 ], [ 22, 30 ],
    [ 23, 30 ], [ 24, 30 ], [ 25, 35 ],
  ]);

  return teamSizeMappings.get(teamSize) || teamSize;
};

const handleMinChange = (): void => {
  if (teamSizeMin.value === null) {
    return;
  }

  if (teamSizeMax.value !== null && teamSizeMax.value < teamSizeMin.value) {
    return;
  }

  teamSizeMax.value = getAutoAdjustedTeamSizeMax(teamSizeMin.value);
};
</script>

<template>
  <div class="m-3 flex flex-col gap-4 lg:m-4">
    <div class="flex flex-col gap-4 lg:flex-row">
      <div class="flex flex-1 flex-col">
        <Typography variant="body-bold">
          {{ trans('search.filters.capacity.min') }}
        </Typography>

        <div
          :class="[
            'relative flex items-center border',
            'lg:w-[167px]'
          ]"
        >
          <input
            ref="minInput"
            v-model.number="teamSizeMin"
            type="number"
            placeholder="1"
            :class="[
              'w-full outline-none',
              'rounded-lg'
            ]"
            @input="handleMinChange"
          >

          <Typography
            variant="body"
            color="gray-dark"
            class="absolute right-3"
          >
            {{ trans('search.filters.capacity.placeholder') }}
          </Typography>
        </div>
      </div>

      <div class="flex flex-1 flex-col">
        <Typography variant="body-bold">
          {{ trans('search.filters.capacity.max') }}
        </Typography>

        <div
          :class="[
            'relative flex items-center border',
            'lg:w-[167px]'
          ]"
        >
          <input
            v-model.number="teamSizeMax"
            type="number"
            placeholder="500+"
            :class="[
              'w-full outline-none',
              'rounded-lg'
            ]"
          >

          <Typography
            variant="body"
            color="gray-dark"
            class="absolute right-3"
          >
            {{ trans('search.filters.capacity.placeholder') }}
          </Typography>
        </div>
      </div>
    </div>
  </div>
</template>
